import { useEffect, useMemo, useState } from 'react';
import { ehComprado, type CriterioRateioDto, type ItemResponse, type OrigemValorResiduo, type SaidaClasseDto, type SaidaFichaResponse, type SalvarSaidasDto } from '@triade/contracts';
import { useSalvarSaidas, useValoresRecuperacao } from '@/api/fichas';
import { useItens } from '@/api/itens';
import { Link } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { avisarDesfazer } from '@/components/desfazer';
import { ItemCombobox, type ItemResumo } from '@/components/item-combobox';
import { Input } from '@/components/ui/input';
import { useDebounced } from '@/hooks/use-debounced';
import { ApiError } from '@/lib/api';
import { decimalDigitado, decimalParaTextoCampo, formatarCusto, formatarData, formatarQuantidade } from '@/lib/format';
import { rotuloUnidade } from '@/lib/i18n-pt';
import { novoUid, textoCampoDecimal } from './tipos';

/** Linha da secao Saidas. Quantidade e valor ficam como o usuario digita (texto pt-BR); `valorRecuperacao` e `credito` vem da API. */
export interface LinhaSaida {
  uid: string;
  classe: SaidaClasseDto;
  itemId: string | null;
  itemCodigo: string | null;
  descricao: string;
  unidade: string;
  quantidade: string;
  /** RESIDUO: custo de destinacao por unidade (texto digitado, pt-BR). */
  valorUnitario: string;
  /** RESIDUO gravado: custo de destinacao como a API devolveu (decimal com ponto), para exibir sem editar. */
  valorApi?: string | null;
  /** SUBPRODUTO gravado: valor de recuperacao vigente e credito por unidade do principal, como a API devolveu. */
  valorRecuperacao?: string | null;
  credito?: string;
  /** Linha que veio gravada da API (false/ausente = linha nova, ainda nao salva). */
  gravada?: boolean;
  /** Classe como foi gravada: o efeito no custo (credito, destinacao) so se mostra enquanto a classe nao muda. */
  classeGravada?: SaidaClasseDto;
  /** RESIDUO com item (Sessao 49, D), como a API devolveu (custo.ver): valor vigente por unidade (null = sem aquisicao vigente). */
  valorVigente?: string | null;
  /** De onde veio o valor vigente (aquisicao ou digitado). So com custo.ver. */
  origemValor?: OrigemValorResiduo;
  /** RESIDUO com item sem aquisicao vigente na data: o motivo (visivel para todos, sem R$). */
  alerta?: { code: 'AQUISICAO_AUSENTE'; message: string };
}

/** Item escolhido para uma saida: nova linha (`uid` nulo) ou linha existente sem item. */
interface AlvoItem {
  uid: string | null;
  classe: 'SUBPRODUTO' | 'COPRODUTO' | 'RESIDUO';
}

/** Campos do valor de um RESIDUO que dependem do item: somem ao trocar ou tirar o item (o valor digitado tambem). */
const SEM_VALOR_DE_RESIDUO = { valorUnitario: '', valorApi: undefined, valorVigente: undefined, origemValor: undefined, alerta: undefined } as const;

const ROTULO_CLASSE: Record<SaidaClasseDto, string> = { SUBPRODUTO: 'Subproduto', RESIDUO: 'Resíduo', COPRODUTO: 'Coproduto' };
const ROTULO_CRITERIO: Record<CriterioRateioDto, string> = { VALOR_VENDA: 'Valor de venda relativo', QUANTIDADE: 'Quantidade física' };

/** Saidas gravadas como linhas da tela. */
export function deSaidas(saidas: SaidaFichaResponse[]): LinhaSaida[] {
  return saidas.map((s) => ({
    uid: novoUid(),
    classe: s.classe,
    itemId: s.itemId,
    itemCodigo: s.itemCodigo,
    descricao: s.descricao,
    unidade: s.unidade ?? '',
    quantidade: formatarQuantidade(s.quantidade) === '—' ? '' : formatarQuantidade(s.quantidade),
    valorUnitario: textoCampoDecimal(s.valorUnitario ?? ''),
    valorApi: s.valorUnitario,
    valorRecuperacao: s.valorRecuperacao,
    credito: s.credito,
    valorVigente: s.valorVigente,
    origemValor: s.origemValor,
    alerta: s.alerta,
    gravada: true,
    classeGravada: s.classe,
  }));
}

/** Origem do valor vigente do residuo com item, em texto (so formato: a data vem da API). Sem valor de origem: null. */
export function origemDoValor(origem: OrigemValorResiduo | undefined): string | null {
  if (!origem) return null;
  if (origem.tipo === 'DIGITADO') return 'digitado';
  const desde = origem.validFrom ? `aquisição vigente desde ${formatarData(origem.validFrom)}` : 'aquisição vigente';
  return origem.preferencial ? `${desde} · fornecedor preferencial` : desde;
}

/**
 * Corpo de PUT /saidas. So formato: texto pt-BR vira decimal com ponto (`decimalDigitado`, sem conta). Erro devolve a mensagem
 * do campo para a tela mostrar antes de enviar. Subproduto e coproduto exigem o item; residuo exige a descricao.
 */
export function paraSaidas(linhas: LinhaSaida[]): { saidas: SalvarSaidasDto['saidas'] } | { erro: string } {
  const saidas: SalvarSaidasDto['saidas'] = [];
  for (const l of linhas) {
    const quantidade = decimalDigitado(l.quantidade);
    const nome = l.descricao.trim() || 'saída';
    if (quantidade === null) return { erro: `Informe a quantidade de ${nome}.` };
    const unidade = l.unidade.trim();
    if (l.classe === 'SUBPRODUTO') {
      if (!l.itemId) return { erro: 'Subproduto: escolha o item do cadastro.' };
      saidas.push({ classe: 'SUBPRODUTO', itemId: l.itemId, descricao: nome, quantidade, ...(unidade ? { unidade } : {}) });
    } else if (l.classe === 'COPRODUTO') {
      if (!l.itemId) return { erro: 'Coproduto: escolha o item do cadastro.' };
      saidas.push({ classe: 'COPRODUTO', itemId: l.itemId, descricao: nome, quantidade, ...(unidade ? { unidade } : {}) });
    } else {
      if (!l.descricao.trim()) return { erro: 'Resíduo: informe a descrição.' };
      // Com item (Sessao 49, D) o custo de destinacao e a aquisicao vigente do item: o valor nao vai (a API recusa valorUnitario com item).
      if (l.itemId) {
        saidas.push({ classe: 'RESIDUO', itemId: l.itemId, descricao: nome, quantidade, ...(unidade ? { unidade } : {}) });
        continue;
      }
      const valor = l.valorUnitario.trim() ? decimalDigitado(l.valorUnitario) : null;
      if (l.valorUnitario.trim() && valor === null) return { erro: `Valor de destinação de ${nome} inválido.` };
      saidas.push({ classe: 'RESIDUO', descricao: nome, quantidade, ...(unidade ? { unidade } : {}), ...(valor !== null ? { valorUnitario: valor } : {}) });
    }
  }
  return { saidas };
}

/**
 * Quantidade que a ficha produz (saida principal, do lote): texto pt-BR para o decimal da API. So validacao: obrigatoria, ate 6
 * casas e maior que zero (a conta fica na API).
 */
export function paraQuantidadeSaida(texto: string): { quantidadeSaida: string } | { erro: string } {
  const q = decimalDigitado(texto);
  if (q === null) return { erro: 'Informe quantas unidades a ficha produz.' };
  if ((q.split('.')[1] ?? '').length > 6) return { erro: 'A quantidade que a ficha produz aceita até 6 casas decimais.' };
  if (/^0*(\.0*)?$/.test(q)) return { erro: 'A ficha precisa produzir mais que zero.' };
  return { quantidadeSaida: q };
}

/** Efeito no custo de uma linha, so com o que a API devolveu (nenhum R$ e calculado aqui). */
function efeitoDe(l: LinhaSaida, comCusto: boolean): string | null {
  if (l.classe === 'COPRODUTO') return 'recebe parte do custo conjunto (ver rateio)';
  if (!comCusto || !l.gravada || l.classeGravada !== l.classe) return null;
  if (l.classe === 'SUBPRODUTO') return l.credito != null ? `abate ${formatarCusto(l.credito)}` : null;
  return l.valorApi != null ? `soma ${formatarCusto(l.valorApi)}` : null;
}

const assinaturaDe = (quantidade: string, criterio: CriterioRateioDto, linhas: LinhaSaida[]) =>
  JSON.stringify({
    quantidade,
    criterio,
    linhas: linhas.map(({ uid: _u, valorRecuperacao: _v, credito: _c, valorApi: _a, gravada: _g, classeGravada: _k, valorVigente: _vv, origemValor: _o, alerta: _al, ...resto }) => resto),
  });

/**
 * Motivo (uma linha) para a secao nao ser editavel, na ordem: ficha em uso, sem custo.ver, sem ficha.editar. Nulo = editavel.
 * So escolhe a mensagem: a regra de edicao continua na tela (podeEditar && comCusto && !emUso).
 */
export function motivoSaidasNaoEditavel({ podeEditar, comCusto, emUso }: { podeEditar: boolean; comCusto: boolean; emUso?: boolean }): string | null {
  if (emUso) return 'Versão em uso: crie uma nova versão para editar as saídas.';
  if (!comCusto) return 'Sem permissão de custo: só quem vê custo edita as saídas.';
  if (!podeEditar) return 'Sem permissão para editar fichas.';
  return null;
}

function mensagemErro(e: unknown): string {
  return e instanceof ApiError ? `${e.message}${e.code ? ` (${e.code})` : ''}` : 'falha de comunicação com a API';
}

export interface SaidasFichaProps {
  fichaId: string;
  /** Item da ficha: linha fixa PRINCIPAL (codigo, descricao e unidade de custo). */
  principal: { codigo: string; descricao: string; unidade: string };
  /** Quantidade que a ficha produz (saida principal, do lote). Ausente = 1. */
  quantidadeSaida?: string;
  /** Critério do rateio (só aparece o seletor com coproduto). Ausente = valor de venda relativo. */
  criterioRateio?: CriterioRateioDto;
  saidas: SaidaFichaResponse[];
  /** ficha.editar, ficha nao em uso e custo.ver. Sem custo.ver a tela nao edita: a API nao devolve o R$ e salvar apagaria o valor. */
  editavel: boolean;
  comCusto: boolean;
  /** Motivo (uma linha) de a secao nao ser editavel; nulo quando e editavel. Calculado pela tela da ficha (`motivoSaidasNaoEditavel`). */
  motivoNaoEditavel: string | null;
  /** Ha alerta de custo da secao Saidas (`alertasCusto` com `secao === 'SAIDAS'`): a secao fica em vermelho. */
  comErro?: boolean;
  /** Mensagem da API para o erro da secao (alertas `SAIDAS` juntos). */
  textoErro?: string;
}

/**
 * Secao "Saidas da ficha" (Sessoes 17 e 31): uma tabela com a saida PRINCIPAL (fixa, com a quantidade que a ficha produz), subprodutos
 * (credito pelo valor de recuperacao vigente), residuos (custo de destinacao) e coprodutos (recebem parte do custo conjunto). Salva
 * tudo de uma vez com PUT /saidas. Nenhum R$ e calculado aqui.
 */
export function SaidasFicha({
  fichaId,
  principal,
  quantidadeSaida,
  criterioRateio,
  saidas,
  editavel,
  comCusto,
  motivoNaoEditavel,
  comErro = false,
  textoErro,
}: SaidasFichaProps) {
  const inicial = useMemo(() => {
    const linhas = deSaidas(saidas);
    const quantidade = decimalParaTextoCampo(quantidadeSaida ?? '1');
    const criterio: CriterioRateioDto = criterioRateio ?? 'VALOR_VENDA';
    return { linhas, quantidade, criterio, assinatura: assinaturaDe(quantidade, criterio, linhas) };
  }, [saidas, quantidadeSaida, criterioRateio]);
  const [linhas, setLinhas] = useState<LinhaSaida[]>(inicial.linhas);
  const [quantidade, setQuantidade] = useState(inicial.quantidade);
  const [criterio, setCriterio] = useState<CriterioRateioDto>(inicial.criterio);
  const [base, setBase] = useState(inicial.assinatura);
  const [alvo, setAlvo] = useState<AlvoItem | null>(null);
  const [termo, setTermo] = useState('');
  const [erro, setErro] = useState<string | null>(null);
  const [ok, setOk] = useState<string | null>(null);
  /** Avisos que a API devolveu no ultimo salvar (`avisosSaidas`). Somem quando a tela volta a ter edicao. */
  const [avisos, setAvisos] = useState<string[]>([]);
  const gravar = useSalvarSaidas();
  const qc = useQueryClient();
  const dirty = assinaturaDe(quantidade, criterio, linhas) !== base;
  // Coproduto (definir ou mudar para ele, e o criterio do rateio) so com custo.ver. A API responde 403 sem a permissao.
  const podeCoproduto = comCusto;
  // Linha COPRODUTO gravada sem custo.ver e so leitura; as demais seguem o `editavel` da tela.
  const editaLinha = (l: LinhaSaida) => editavel && (podeCoproduto || l.classe !== 'COPRODUTO');

  // Qualquer edicao em curso apaga os avisos do ultimo salvar.
  useEffect(() => {
    if (dirty) setAvisos([]);
  }, [dirty]);

  // O servidor mudou (salvou ou recarregou): a tela volta ao gravado, se nao houver edicao em curso.
  useEffect(() => {
    if (dirty) return;
    setLinhas(inicial.linhas);
    setQuantidade(inicial.quantidade);
    setCriterio(inicial.criterio);
    setBase(inicial.assinatura);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inicial]);

  const busca = useDebounced(termo.trim(), 250);
  const itens = useItens({ q: busca || undefined, ativo: true, limit: 20 });
  const candidatos = useMemo<ItemResponse[]>(() => itens.data ?? [], [itens.data]);
  // Residuo com item: so os comprados (a API recusa os demais com 422 RESIDUO_ITEM_TIPO_NAO_PERMITIDO).
  const comprados = useMemo<ItemResponse[]>(() => candidatos.filter((i) => ehComprado(i.tipo)), [candidatos]);
  const valores = useValoresRecuperacao({ ativo: comCusto });
  // Hipotetico: so o valor vigente do item (validTo nulo) conta para a marca.
  const hipoteticos = useMemo(() => new Set((valores.data ?? []).filter((v) => v.validTo === null && v.hipotetico).map((v) => v.itemId)), [valores.data]);
  const temCoproduto = linhas.some((l) => l.classe === 'COPRODUTO');

  function abrirBusca(classe: 'SUBPRODUTO' | 'COPRODUTO') {
    setAlvo((a) => (a && a.uid === null && a.classe === classe ? null : { uid: null, classe }));
    setTermo('');
  }

  function escolherItem(escolhido: ItemResumo) {
    if (!alvo) return;
    const completo = candidatos.find((i) => i.id === escolhido.id);
    const dados = { itemId: escolhido.id, itemCodigo: escolhido.codigo, descricao: escolhido.descricao, unidade: completo?.unidade ?? '' };
    if (alvo.uid === null) {
      const classe = alvo.classe;
      setLinhas((atual) => [...atual, { uid: novoUid(), classe, ...dados, quantidade: '', valorUnitario: '' }]);
    } else {
      const uid = alvo.uid;
      setLinhas((atual) => atual.map((l) => (l.uid === uid ? { ...l, ...dados, ...(l.classe === 'RESIDUO' ? SEM_VALOR_DE_RESIDUO : {}) } : l)));
    }
    setAlvo(null);
    setTermo('');
  }

  function adicionarResiduo() {
    setLinhas((atual) => [...atual, { uid: novoUid(), classe: 'RESIDUO', itemId: null, itemCodigo: null, descricao: '', unidade: '', quantidade: '', valorUnitario: '' }]);
  }

  function mudarClasse(uid: string, classe: SaidaClasseDto) {
    setLinhas((atual) =>
      atual.map((l) => {
        if (l.uid !== uid) return l;
        // Sair do residuo com item leva o item e o valor vigente dele; virar residuo comeca sem item.
        const ajuste =
          l.classe === 'RESIDUO' && classe !== 'RESIDUO'
            ? { itemId: null, itemCodigo: null, valorVigente: undefined, origemValor: undefined, alerta: undefined }
            : classe === 'RESIDUO'
              ? { itemId: null, itemCodigo: null }
              : {};
        return { ...l, classe, ...ajuste };
      }),
    );
    setAlvo((a) => (a?.uid === uid ? null : a));
  }

  function tirarItem(uid: string) {
    setLinhas((atual) => atual.map((l) => (l.uid === uid ? { ...l, itemId: null, itemCodigo: null, ...SEM_VALOR_DE_RESIDUO } : l)));
    setAlvo((a) => (a?.uid === uid ? null : a));
  }

  function editar(uid: string, campo: 'descricao' | 'unidade' | 'quantidade' | 'valorUnitario', valor: string) {
    setLinhas((atual) => atual.map((l) => (l.uid === uid ? { ...l, [campo]: valor } : l)));
  }

  async function salvar() {
    setErro(null);
    setOk(null);
    const qtd = paraQuantidadeSaida(quantidade);
    if ('erro' in qtd) return setErro(qtd.erro);
    const pronto = paraSaidas(linhas);
    if ('erro' in pronto) return setErro(pronto.erro);
    try {
      const gravada = await gravar.mutateAsync({ fichaId, saidas: pronto.saidas, quantidadeSaida: qtd.quantidadeSaida, criterioRateio: criterio });
      void avisarDesfazer({ recurso: 'fichas_tecnicas', chave: fichaId, qc });
      const novasLinhas = deSaidas(gravada.saidas ?? []);
      const novaQuantidade = decimalParaTextoCampo(gravada.quantidadeSaida ?? qtd.quantidadeSaida);
      const novoCriterio: CriterioRateioDto = gravada.criterioRateio ?? criterio;
      setLinhas(novasLinhas);
      setQuantidade(novaQuantidade);
      setCriterio(novoCriterio);
      setBase(assinaturaDe(novaQuantidade, novoCriterio, novasLinhas));
      setAvisos((gravada.avisosSaidas ?? []).map((a) => a.message));
      setOk('Saídas gravadas.');
    } catch (e) {
      setErro(mensagemErro(e));
    }
  }

  function desfazer() {
    setLinhas(inicial.linhas);
    setQuantidade(inicial.quantidade);
    setCriterio(inicial.criterio);
    setBase(inicial.assinatura);
    setAlvo(null);
    setErro(null);
    setAvisos([]);
  }

  const campo = 'h-8 bg-card text-small';

  return (
    <section
      aria-label="Saídas da ficha"
      data-testid="saidas-ficha"
      className={comErro ? 'rounded-2xl border border-destructive/40 bg-danger-bg' : 'rounded-2xl border border-border bg-card'}
    >
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-border px-5 py-4">
        <div>
          <h2 className="text-title font-semibold">Saídas da ficha</h2>
          <div className="text-small text-ink-muted">
            Subproduto sai da produção com valor de recuperação e abate o custo do principal; resíduo sai com custo de destinação e soma ao custo. Valores por unidade do principal.
          </div>
          <div className="text-caption text-ink-muted">
            As quantidades da ficha e das saídas são do lote; o custo unitário é o custo do lote ÷ N.
          </div>
        </div>
        {editavel && (
          <div className="flex gap-2">
            <button type="button" onClick={() => abrirBusca('SUBPRODUTO')} className="h-[34px] rounded-[9px] border border-border bg-card px-3 text-small font-medium hover:bg-line-soft">
              + Subproduto
            </button>
            {podeCoproduto && (
              <button type="button" onClick={() => abrirBusca('COPRODUTO')} className="h-[34px] rounded-[9px] border border-border bg-card px-3 text-small font-medium hover:bg-line-soft">
                + Coproduto
              </button>
            )}
            <button type="button" onClick={adicionarResiduo} className="h-[34px] rounded-[9px] border border-border bg-card px-3 text-small font-medium hover:bg-line-soft">
              + Resíduo
            </button>
          </div>
        )}
      </div>

      {comErro && (
        <div role="alert" data-testid="saidas-erro" className="border-b border-destructive/30 px-5 py-3 text-small text-danger-ink">
          <div className="font-semibold">Esta seção tem erro</div>
          {textoErro && <div className="mt-1">{textoErro}</div>}
        </div>
      )}

      {!editavel && motivoNaoEditavel && (
        <p role="status" className="border-b border-border px-5 py-2 text-small text-warning-ink">
          {motivoNaoEditavel}
        </p>
      )}

      {editavel && alvo && (
        <div className="border-b border-border px-5 py-3">
          <div className="flex max-w-xl flex-col gap-1 text-caption font-medium text-ink-muted">
            {alvo.uid === null ? (alvo.classe === 'COPRODUTO' ? 'Escolher o item coproduto' : 'Escolher o item subproduto') : alvo.classe === 'RESIDUO' ? 'Escolher o item do resíduo' : 'Escolher o item da saída'}
            <ItemCombobox
              valor={null}
              onSelecionar={escolherItem}
              itens={alvo.classe === 'RESIDUO' ? comprados : candidatos}
              carregando={itens.isFetching}
              erro={itens.error?.message ?? null}
              termo={termo}
              onTermoChange={setTermo}
              placeholder="Buscar item do cadastro (código ou descrição)…"
              ariaLabel={alvo.uid === null ? (alvo.classe === 'COPRODUTO' ? 'Adicionar coproduto' : 'Adicionar subproduto') : alvo.classe === 'RESIDUO' ? 'Escolher item do resíduo' : 'Escolher item da saída'}
            />
          </div>
        </div>
      )}

      <div className="overflow-x-auto px-5 py-3">
        <table className="w-full min-w-[720px] border-collapse text-small">
          <thead>
            <tr className="text-left text-caption text-ink-muted">
              <th scope="col" className="py-2 pr-3 font-medium">Classe</th>
              <th scope="col" className="py-2 pr-3 font-medium">Item ou descrição</th>
              <th scope="col" className="py-2 pr-3 font-medium">Quantidade</th>
              <th scope="col" className="py-2 pr-3 font-medium">Un.</th>
              {comCusto && <th scope="col" className="py-2 pr-3 text-right font-medium">Valor de destinação (R$)</th>}
              <th scope="col" className="py-2 pr-3 font-medium">Efeito no custo</th>
              {editavel && <th scope="col" className="w-10" />}
            </tr>
          </thead>
          <tbody>
            <tr className="border-t border-line-soft" data-testid="saida-principal">
              <td className="py-2 pr-3 font-medium">Principal</td>
              <td className="py-2 pr-3">
                <span className="flex items-center gap-2">
                  {principal.codigo && <span className="rounded bg-line-soft px-1.5 text-caption font-medium">{principal.codigo}</span>}
                  <span>{principal.descricao}</span>
                </span>
              </td>
              <td className="py-2 pr-3">
                {editavel ? (
                  <div className="flex flex-col gap-1">
                    <span className="text-caption text-ink-muted">Esta ficha produz</span>
                    <Input aria-label="Quantidade produzida pela ficha" inputMode="decimal" className={`${campo} tabular w-24`} value={quantidade} onChange={(e) => setQuantidade(e.target.value)} />
                  </div>
                ) : (
                  <span className="flex flex-col gap-1">
                    <span className="text-caption text-ink-muted">Esta ficha produz</span>
                    <span className="tabular">{quantidade || '—'}</span>
                  </span>
                )}
              </td>
              <td className="py-2 pr-3">{rotuloUnidade(principal.unidade)}</td>
              <td className="py-2 pr-3" />
              {comCusto && <td className="py-2 pr-3" />}
              <td className="py-2 pr-3" />
              {editavel && <td className="py-2 text-right" />}
            </tr>

            {linhas.map((l, i) => (
              <tr key={l.uid} className="border-t border-line-soft" data-testid={`saida-${l.classe.toLowerCase()}`}>
                <td className="py-2 pr-3">
                  {editaLinha(l) ? (
                    <select
                      aria-label={`Classe da saída ${i + 1}`}
                      value={l.classe}
                      onChange={(e) => mudarClasse(l.uid, e.target.value as SaidaClasseDto)}
                      className="h-8 rounded-md border border-border bg-card px-2 text-small"
                    >
                      <option value="SUBPRODUTO">Subproduto</option>
                      <option value="RESIDUO">Resíduo</option>
                      {podeCoproduto && <option value="COPRODUTO">Coproduto</option>}
                    </select>
                  ) : (
                    ROTULO_CLASSE[l.classe]
                  )}
                </td>
                <td className="py-2 pr-3">
                  {l.classe === 'RESIDUO' ? (
                    <span className="flex flex-col gap-1">
                      {editavel ? (
                        <Input aria-label={`Descrição do resíduo ${i + 1}`} className={campo} maxLength={200} value={l.descricao} onChange={(e) => editar(l.uid, 'descricao', e.target.value)} />
                      ) : (
                        <span>{l.descricao}</span>
                      )}
                      {l.itemId && (
                        <span className="flex flex-wrap items-center gap-2 text-caption">
                          {l.itemCodigo && <span className="rounded bg-line-soft px-1.5 font-medium">{l.itemCodigo}</span>}
                          {editaLinha(l) && (
                            <button type="button" aria-label={`Tirar item do resíduo ${i + 1}`} onClick={() => tirarItem(l.uid)} className="rounded-md border border-border bg-card px-2 py-0.5 font-medium hover:bg-line-soft">
                              Tirar item
                            </button>
                          )}
                        </span>
                      )}
                      {!l.itemId && editaLinha(l) && (
                        <button
                          type="button"
                          aria-label={`Escolher item do resíduo ${i + 1}`}
                          onClick={() => setAlvo({ uid: l.uid, classe: 'RESIDUO' })}
                          className="w-fit rounded-md border border-border bg-card px-2 py-0.5 text-caption font-medium hover:bg-line-soft"
                        >
                          Escolher item (opcional)
                        </button>
                      )}
                      {l.alerta && (
                        <span role="status" className="text-caption text-warning-ink">
                          {l.alerta.message}
                        </span>
                      )}
                    </span>
                  ) : l.itemId ? (
                    <span className="flex flex-col gap-1">
                      <span className="flex items-center gap-2">
                        {l.itemCodigo && <span className="rounded bg-line-soft px-1.5 text-caption font-medium">{l.itemCodigo}</span>}
                        <span>{l.descricao}</span>
                        {l.classe === 'SUBPRODUTO' && l.itemId && hipoteticos.has(l.itemId) && (
                          <span className="rounded-full bg-warning-bg px-2 py-0.5 text-caption font-semibold text-warning-ink" data-testid="saida-hipotetico">
                            hipotético
                          </span>
                        )}
                      </span>
                      {comCusto && l.gravada && l.classeGravada === 'SUBPRODUTO' && l.valorRecuperacao == null && l.itemId && (
                        <Link to="/cadastros/itens/$id" params={{ id: l.itemId }} search={{ aba: 'recuperacao' }} className="text-caption font-medium text-warning-ink underline">
                          Subproduto sem valor de recuperação: cadastre em Cadastros › Produtos e serviços › aba Valor de recuperação
                        </Link>
                      )}
                    </span>
                  ) : editaLinha(l) ? (
                    <span className="flex flex-col gap-1">
                      <span className="text-caption text-warning-ink">Escolha o item do cadastro</span>
                      <button
                        type="button"
                        aria-label={`Escolher item da saída ${i + 1}`}
                        onClick={() => setAlvo({ uid: l.uid, classe: l.classe === 'COPRODUTO' ? 'COPRODUTO' : 'SUBPRODUTO' })}
                        className="w-fit rounded-md border border-border bg-card px-2 py-0.5 text-caption font-medium hover:bg-line-soft"
                      >
                        Escolher item
                      </button>
                    </span>
                  ) : (
                    l.descricao
                  )}
                </td>
                <td className="py-2 pr-3">
                  {editaLinha(l) ? (
                    <Input aria-label={`Quantidade da saída ${i + 1}`} inputMode="decimal" className={`${campo} tabular w-24`} value={l.quantidade} onChange={(e) => editar(l.uid, 'quantidade', e.target.value)} />
                  ) : (
                    <span className="tabular">{l.quantidade || '—'}</span>
                  )}
                </td>
                <td className="py-2 pr-3">
                  {editaLinha(l) ? (
                    <Input aria-label={`Unidade da saída ${i + 1}`} maxLength={10} className={`${campo} w-20`} value={l.unidade} onChange={(e) => editar(l.uid, 'unidade', e.target.value)} />
                  ) : (
                    rotuloUnidade(l.unidade)
                  )}
                </td>
                {comCusto && (
                  <td className="tabular py-2 pr-3 text-right">
                    {l.classe === 'RESIDUO' && l.itemId ? (
                      // Com item: o valor nao e digitado. So aparece quando a API trouxe (valorVigente, custo.ver).
                      l.valorVigente !== undefined && (
                        <span className="flex flex-col items-end gap-0.5" data-testid="residuo-valor-vigente">
                          <span>{l.valorVigente === null ? 'Valor vigente: —' : `Valor vigente: ${formatarCusto(l.valorVigente)}/${l.unidade.trim() ? rotuloUnidade(l.unidade) : 'un'}`}</span>
                          {origemDoValor(l.origemValor) && <span className="text-caption text-ink-muted">{origemDoValor(l.origemValor)}</span>}
                        </span>
                      )
                    ) : l.classe === 'RESIDUO' && editavel ? (
                      <Input aria-label={`Valor de destinação do resíduo ${i + 1}`} inputMode="decimal" className={`${campo} tabular w-28 text-right`} value={l.valorUnitario} onChange={(e) => editar(l.uid, 'valorUnitario', e.target.value)} />
                    ) : null}
                  </td>
                )}
                <td className="py-2 pr-3">{efeitoDe(l, comCusto)}</td>
                {editavel && (
                  <td className="py-2 text-right">
                    {editaLinha(l) && (
                      <button type="button" aria-label={`Remover saída ${i + 1}`} onClick={() => setLinhas((atual) => atual.filter((x) => x.uid !== l.uid))} className="rounded-md px-2 text-ink-muted hover:bg-line-soft hover:text-danger-ink">
                        ×
                      </button>
                    )}
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </table>
        {linhas.length === 0 && (
          <p role="status" className="px-5 py-4 text-center text-small text-ink-muted">
            Esta ficha não tem subprodutos, coprodutos nem resíduos.
          </p>
        )}
      </div>

      {avisos.length > 0 && (
        <div className="flex flex-col gap-2 px-5 pb-3">
          {avisos.map((aviso) => (
            <p key={aviso} role="status" className="rounded-xl border border-warning-dot/40 bg-warning-bg px-4 py-2 text-small text-warning-ink">
              {aviso}
            </p>
          ))}
        </div>
      )}

      {temCoproduto && (
        <div className="flex flex-wrap items-center gap-3 border-t border-border px-5 py-3 text-small">
          {editavel && podeCoproduto ? (
            <label className="flex items-center gap-2">
              <span className="font-medium">Critério do rateio</span>
              <select aria-label="Critério do rateio" value={criterio} onChange={(e) => setCriterio(e.target.value as CriterioRateioDto)} className="h-8 rounded-md border border-border bg-card px-2 text-small">
                <option value="VALOR_VENDA">Valor de venda relativo</option>
                <option value="QUANTIDADE">Quantidade física</option>
              </select>
            </label>
          ) : (
            <span>Critério do rateio: {ROTULO_CRITERIO[criterio]}</span>
          )}
        </div>
      )}

      {erro && (
        <div role="alert" className="mx-5 mb-2 rounded-xl border border-destructive/30 bg-danger-bg px-4 py-3 text-small text-danger-ink">
          {erro}
        </div>
      )}
      {ok && !dirty && (
        <p role="status" className="px-5 pb-2 text-small text-success-ink">
          {ok}
        </p>
      )}
      {editavel && dirty && (
        <div className="flex items-center gap-2 border-t border-border px-5 py-3">
          <button type="button" onClick={() => void salvar()} disabled={gravar.isPending} className="h-9 rounded-[10px] bg-primary px-3.5 text-small font-semibold text-primary-foreground hover:bg-primary/90 disabled:opacity-50">
            {gravar.isPending ? 'Salvando…' : 'Salvar saídas'}
          </button>
          <button type="button" onClick={desfazer} className="h-9 rounded-[10px] border border-border bg-card px-3.5 text-small hover:bg-line-soft">
            Descartar
          </button>
        </div>
      )}
    </section>
  );
}
